// @ts-strict-ignore
import { DashboardCard } from "@dashboard/components/Card";
import ControlledCheckbox from "@dashboard/components/ControlledCheckbox";
import VerticalSpacer from "@dashboard/components/VerticalSpacer";
import { useShippingZoneConfig } from "@dashboard/shipping/hooks/useShippingZoneConfig";
import { InputAdornment, TextField } from "@material-ui/core";
import { makeStyles } from "@saleor/macaw-ui";
import { Text } from "@saleor/macaw-ui-next";
import { useEffect, useState } from "react";
import { defineMessages, FormattedMessage, useIntl } from "react-intl";

const useStyles = makeStyles(
  theme => ({
    grid: {
      display: "grid",
      gridColumnGap: theme.spacing(2),
      gridTemplateColumns: "1fr 1fr 1fr",
    },
    gridTwo: {
      display: "grid",
      gridColumnGap: theme.spacing(2),
      gridTemplateColumns: "1fr 1fr",
    },
  }),
  { name: "ShippingZoneSettings" },
);

const messages = defineMessages({
  dimensionsTitle: { id: "DT5yyp", defaultMessage: "Package Dimensions" },
  dimensionsInfo: {
    id: "7/Puf3",
    defaultMessage: "Applies to every rate in this zone, price- and weight-based alike",
  },
  maxHeight: { id: "gpp8a6", defaultMessage: "Max. Height" },
  maxWidth: { id: "GovN+h", defaultMessage: "Max. Width" },
  maxLength: { id: "DRVvTp", defaultMessage: "Max. Length" },
  maxVolume: { id: "j8kczL", defaultMessage: "Max. Volume" },
  volumetricTitle: { id: "BrGEfb", defaultMessage: "Volumetric Weight" },
  volumetricInfo: {
    id: "uk51nb",
    defaultMessage:
      "Chargeable weight = MAX(real weight, volumetric weight). Volumetric weight = (H × W × L) ÷ divisor.",
  },
  formula: { id: "QZEMm+", defaultMessage: "Formula" },
  save: { id: "jvo0vs", defaultMessage: "Save" },
});

interface ShippingZoneSettingsProps {
  zoneName?: string;
}

export const ShippingZoneSettings = ({ zoneName }: ShippingZoneSettingsProps) => {
  const intl = useIntl();
  const classes = useStyles({});
  const { zone, saving, save } = useShippingZoneConfig(zoneName ?? "");

  const [dimensionsOn, setDimensionsOn] = useState(false);
  const [maxHeight, setMaxHeight] = useState("");
  const [maxWidth, setMaxWidth] = useState("");
  const [maxLength, setMaxLength] = useState("");
  const [maxVolume, setMaxVolume] = useState("");

  const [volumetricOn, setVolumetricOn] = useState(false);
  const [formula, setFormula] = useState("");

  const [initialised, setInitialised] = useState(false);

  useEffect(() => {
    if (!zone || initialised) return;

    const hasDimensions =
      zone.max_package_height_cm !== null ||
      zone.max_package_width_cm !== null ||
      zone.max_package_length_cm !== null ||
      zone.max_package_volume_cm3 !== null;

    setDimensionsOn(hasDimensions);
    setMaxHeight(zone.max_package_height_cm?.toString() ?? "");
    setMaxWidth(zone.max_package_width_cm?.toString() ?? "");
    setMaxLength(zone.max_package_length_cm?.toString() ?? "");
    setMaxVolume(zone.max_package_volume_cm3?.toString() ?? "");
    setVolumetricOn(!!zone.volumetric_formula);
    setFormula(zone.volumetric_formula ?? "");
    setInitialised(true);
  }, [zone, initialised]);

  const handleSave = () => {
    save({
      max_package_height_cm: dimensionsOn && maxHeight ? parseFloat(maxHeight) : null,
      max_package_width_cm: dimensionsOn && maxWidth ? parseFloat(maxWidth) : null,
      max_package_length_cm: dimensionsOn && maxLength ? parseFloat(maxLength) : null,
      max_package_volume_cm3: dimensionsOn && maxVolume ? parseFloat(maxVolume) : null,
      volumetric_formula: volumetricOn ? formula : "",
    } as any);
  };

  return (
    <>
      {/* ── Package Dimensions ─────────────────────────────────────────── */}
      <DashboardCard>
        <DashboardCard.Header>
          <DashboardCard.Title>{intl.formatMessage(messages.dimensionsTitle)}</DashboardCard.Title>
        </DashboardCard.Header>
        <DashboardCard.Content>
          <ControlledCheckbox
            name="dimensionsOn"
            label={
              <>
                <FormattedMessage id="Q2ccPV" defaultMessage="Restrict package dimensions" />
                <Text size={2} fontWeight="light" display="block">
                  {intl.formatMessage(messages.dimensionsInfo)}
                </Text>
              </>
            }
            checked={dimensionsOn}
            onChange={e => setDimensionsOn(!!e.target.value)}
            disabled={false}
          />
          {dimensionsOn && (
            <>
              <VerticalSpacer spacing={2} />
              <div className={classes.grid}>
                <TextField
                  fullWidth
                  label={intl.formatMessage(messages.maxHeight)}
                  type="number"
                  InputProps={{
                    inputProps: { min: 0 },
                    endAdornment: (
                      <InputAdornment position="end">
                        <Text>cm</Text>
                      </InputAdornment>
                    ),
                  }}
                  value={maxHeight}
                  onChange={e => setMaxHeight(e.target.value)}
                />
                <TextField
                  fullWidth
                  label={intl.formatMessage(messages.maxWidth)}
                  type="number"
                  InputProps={{
                    inputProps: { min: 0 },
                    endAdornment: (
                      <InputAdornment position="end">
                        <Text>cm</Text>
                      </InputAdornment>
                    ),
                  }}
                  value={maxWidth}
                  onChange={e => setMaxWidth(e.target.value)}
                />
                <TextField
                  fullWidth
                  label={intl.formatMessage(messages.maxLength)}
                  type="number"
                  InputProps={{
                    inputProps: { min: 0 },
                    endAdornment: (
                      <InputAdornment position="end">
                        <Text>cm</Text>
                      </InputAdornment>
                    ),
                  }}
                  value={maxLength}
                  onChange={e => setMaxLength(e.target.value)}
                />
              </div>
              <VerticalSpacer spacing={2} />
              <div className={classes.gridTwo}>
                <TextField
                  fullWidth
                  label={intl.formatMessage(messages.maxVolume)}
                  type="number"
                  InputProps={{
                    inputProps: { min: 0 },
                    endAdornment: (
                      <InputAdornment position="end">
                        <Text>cm³</Text>
                      </InputAdornment>
                    ),
                  }}
                  value={maxVolume}
                  onChange={e => setMaxVolume(e.target.value)}
                />
              </div>
            </>
          )}
        </DashboardCard.Content>
      </DashboardCard>

      {/* ── Volumetric Weight ──────────────────────────────────────────── */}
      <DashboardCard>
        <DashboardCard.Header>
          <DashboardCard.Title>{intl.formatMessage(messages.volumetricTitle)}</DashboardCard.Title>
        </DashboardCard.Header>
        <DashboardCard.Content>
          <ControlledCheckbox
            name="volumetricOn"
            label={
              <>
                <FormattedMessage id="CcSmX+" defaultMessage="Use volumetric weight" />
                <Text size={2} fontWeight="light" display="block">
                  {intl.formatMessage(messages.volumetricInfo)}
                </Text>
              </>
            }
            checked={volumetricOn}
            onChange={e => setVolumetricOn(!!e.target.value)}
            disabled={false}
          />
          {volumetricOn && (
            <>
              <VerticalSpacer spacing={2} />
              <div className={classes.gridTwo}>
                <TextField
                  fullWidth
                  label={intl.formatMessage(messages.formula)}
                  value={formula}
                  onChange={e => setFormula(e.target.value)}
                  placeholder="e.g. H*W*L/5000"
                  helperText="Volumetric weight = H × W × L ÷ divisor"
                />
              </div>
            </>
          )}
        </DashboardCard.Content>
      </DashboardCard>

      {/* ── Save ──────────────────────────────────────────────────────── */}
      <DashboardCard>
        <DashboardCard.Content>
          <div style={{ display: "flex", justifyContent: "flex-end" }}>
            <button
              onClick={handleSave}
              disabled={saving}
              style={{
                padding: "6px 16px",
                fontSize: 13,
                fontWeight: 600,
                borderRadius: 6,
                border: "none",
                backgroundColor: saving ? "#94a3b8" : "#1d4ed8",
                color: "#fff",
                cursor: saving ? "not-allowed" : "pointer",
              }}
            >
              {saving ? "Saving…" : intl.formatMessage(messages.save)}
            </button>
          </div>
        </DashboardCard.Content>
      </DashboardCard>
    </>
  );
};

export default ShippingZoneSettings;
